import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import Icon from './Icon';
import { fetchShareholderDashboard } from '../shareholderDashboardApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './Dashboard.css';
import './ShareholderDashboard.css';

const AVATAR_COLORS = ['#02c3a6', '#00334a', '#02c3a6', '#02c3a6', '#02c3a6', '#78909c'];

export default function ShareholderDashboard() {
  const { language } = useLanguage();
  const t = useT();
  const navigate = useNavigate();

  /* number locale per language */
  const numLocale = language === 'ar' || language === 'ur' ? 'ar-SA'
    : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const payload = await fetchShareholderDashboard();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const myOwnership      = Number(data?.myOwnership ?? 0);
  const totalShares      = Number(data?.totalShares ?? 0);
  const myShares         = Number(data?.myShares ?? 0);
  const dividendsHistory = useMemo(() => data?.dividendsHistory ?? [], [data]);
  const recentDecisions  = useMemo(() => data?.recentDecisions ?? [], [data]);
  const shareholders     = useMemo(() => data?.shareholders ?? [], [data]);
  const totalDivs        = Number(data?.totalDividends ?? 0);
  const lastDiv          = Number(data?.lastDividend ?? 0);
  const pendingCount     = Number(data?.pendingDecisionsCount ?? 0);
  const shareholdersCount = Number(data?.shareholdersCount ?? 0);

  const ownershipChart = useMemo(() => ({
    series: shareholders.map((s) => Number(s.pct ?? 0)),
    options: {
      chart: { type: 'donut', fontFamily: 'Cairo, sans-serif' },
      labels: shareholders.map((s) => s.name),
      colors: ['#02c3a6', '#00334a', '#02c3a6', '#02c3a6', '#02c3a6', '#90a4ae'],
      dataLabels: { enabled: true, style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' }, formatter: (v) => v.toFixed(1) + '%' },
      legend: { position: 'bottom', fontFamily: 'Cairo, sans-serif', fontSize: '12px' },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: t('shareholder.donutMyStake'),
                fontFamily: 'Cairo, sans-serif',
                fontSize: '13px',
                color: '#02c3a6',
                formatter: () => `${myOwnership}%`,
              },
            },
          },
        },
      },
      stroke: { width: 2, colors: ['#fff'] },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    },
  }), [shareholders, myOwnership, t]);

  const dividendsChart = useMemo(() => ({
    series: [{ name: t('shareholder.divSeriesName'), data: dividendsHistory.map((d) => Number(d.amount ?? 0)) }],
    options: {
      chart: { type: 'area', toolbar: { show: false }, fontFamily: 'Cairo, sans-serif' },
      colors: ['#02c3a6'],
      fill: { type: 'solid', opacity: 0.3 },
      stroke: { curve: 'smooth', width: 2.5 },
      xaxis: { categories: dividendsHistory.map((d) => d.quarter), labels: { style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' } } },
      yaxis: { labels: { style: { fontFamily: 'Cairo, sans-serif' }, formatter: (v) => v.toLocaleString(numLocale) + t('shareholder.sarShort') } },
      dataLabels: { enabled: false },
      grid: { borderColor: '#f0eaf8', strokeDashArray: 4 },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' }, y: { formatter: (v) => `${v.toLocaleString(numLocale)} ${t('shareholder.sarWord')}` } },
      markers: { size: 5, colors: ['#02c3a6'], strokeColors: '#fff', strokeWidth: 2 },
    },
  }), [dividendsHistory, numLocale, t]);

  const decisionsStatusChart = useMemo(() => ({
    series: data?.decisionStatusSeries ?? [0, 0, 0],
    options: {
      chart: { type: 'donut', fontFamily: 'Cairo, sans-serif' },
      labels: [t('shareholder.chartApproved'), t('shareholder.chartPending'), t('shareholder.chartRejected')],
      colors: ['#02c3a6', '#f57c00', '#c62828'],
      dataLabels: { style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' } },
      legend: { position: 'bottom', fontFamily: 'Cairo, sans-serif', fontSize: '12px' },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              total: {
                show: true,
                label: t('shareholder.donutTotal'),
                fontFamily: 'Cairo, sans-serif',
                formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0),
              },
            },
          },
        },
      },
      stroke: { width: 2, colors: ['#fff'] },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' } },
    },
  }), [data, t]);

  const dividendPctChart = useMemo(() => ({
    series: [{ name: t('shareholder.yieldSeriesName'), data: dividendsHistory.map((d) => Number(d.yieldPct ?? 0)) }],
    options: {
      chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Cairo, sans-serif' },
      plotOptions: { bar: { borderRadius: 8, columnWidth: '50%', distributed: true } },
      colors: ['#02c3a6', '#02c3a6', '#02c3a6', '#02c3a6', '#02c3a6'],
      xaxis: { categories: dividendsHistory.map((d) => d.quarter), labels: { style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' } } },
      yaxis: { labels: { style: { fontFamily: 'Cairo, sans-serif' }, formatter: (v) => `${v}%` } },
      dataLabels: { enabled: true, style: { fontFamily: 'Cairo, sans-serif', fontSize: '11px' }, formatter: (v) => `${v}%` },
      grid: { borderColor: '#f0eaf8', strokeDashArray: 4 },
      legend: { show: false },
      tooltip: { style: { fontFamily: 'Cairo, sans-serif' }, y: { formatter: (v) => `${v}%` } },
    },
  }), [dividendsHistory, t]);

  const decisionStatusMap = useMemo(() => ({
    pending:  { label: t('shareholder.statusPending'),  cls: 'status-badge--pending'  },
    approved: { label: t('shareholder.statusApproved'), cls: 'status-badge--approved' },
    rejected: { label: t('shareholder.statusRejected'), cls: 'status-badge--rejected' },
  }), [t]);

  const impactMap = useMemo(() => ({
    high:   { label: t('shareholder.impactHigh'),   color: '#c62828', bg: '#fce4ec' },
    medium: { label: t('shareholder.impactMedium'), color: '#e65100', bg: '#fff3e0' },
    low:    { label: t('shareholder.impactLow'),    color: '#02c3a6', bg: '#e0f7f4' },
  }), [t]);

  return (
    <div className="shdash">
      {loadError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10 }}>{loadError}</div>}
      {loading && <div style={{ marginBottom: 12, color: '#78909c' }}>{t('shareholder.loading')}</div>}

      <div className="shdash__welcome">
        <div className="shdash__welcome-left">
          <div className="shdash__welcome-avatar">SH</div>
          <div>
            <div className="shdash__welcome-greeting">{t('shareholder.welcomeHi')}</div>
            <div className="shdash__welcome-name">{t('shareholder.welcomeSub')}</div>
          </div>
        </div>
        <div className="shdash__welcome-chips">
          <div className="shdash__welcome-chip">
            <div className="shdash__welcome-chip-label">{t('shareholder.chipMyStake')}</div>
            <div className="shdash__welcome-chip-value">{myOwnership}%</div>
          </div>
          <div className="shdash__welcome-chip">
            <div className="shdash__welcome-chip-label">{t('shareholder.chipShares')}</div>
            <div className="shdash__welcome-chip-value">{myShares.toLocaleString(numLocale)}</div>
          </div>
          <div className="shdash__welcome-chip shdash__welcome-chip--green">
            <div className="shdash__welcome-chip-label">{t('shareholder.chipLastDiv')}</div>
            <div className="shdash__welcome-chip-value">{lastDiv.toLocaleString(numLocale)}{t('shareholder.sarShort')}</div>
          </div>
        </div>
      </div>

      <div className="shdash__stats">
        {[
          { icon: 'money',     label: t('shareholder.statStake'),     value: `${myOwnership}%`,                        sub: `${myShares.toLocaleString(numLocale)} ${t('shareholder.sharesWord')}`,       color: '#02c3a6', bg: '#e0f7f4', border: '#c8f0ea' },
          { icon: 'chart',     label: t('shareholder.statDivTotal'),  value: totalDivs.toLocaleString(numLocale),       sub: t('shareholder.sarWord'),                                                     color: '#02c3a6', bg: '#e0f7f4', border: '#c8f0ea' },
          { icon: 'decisions', label: t('shareholder.statPending'),   value: String(pendingCount),                      sub: t('shareholder.statPendingSub'),                                              color: '#02c3a6', bg: '#e0f7f4', border: '#c8f0ea' },
          { icon: 'board',     label: t('shareholder.statInvestors'), value: String(shareholdersCount),                 sub: `${totalShares.toLocaleString(numLocale)} ${t('shareholder.sharesWordTotal')}`, color: '#02c3a6', bg: '#e0f7f4', border: '#c8f0ea' },
        ].map((s, i) => (
          <div
            key={i}
            className="shdash__stat-card"
            style={{ '--sc-color': s.color, '--sc-bg': s.bg, '--sc-border': s.border, animationDelay: `${i * 0.08}s` }}
          >
            <div className="shdash__stat-icon">
              <Icon name={s.icon} size={22} color={s.color} strokeWidth={1.6} />
            </div>
            <div className="shdash__stat-body">
              <div className="shdash__stat-label">{s.label}</div>
              <div className="shdash__stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="shdash__stat-sub">{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="shdash__charts-row">
        <div className="shdash__chart-card shdash__chart-card--md">
          <div className="shdash__chart-title">
            <Icon name="money" size={15} color="#02c3a6" />
            {t('shareholder.chartOwnership')}
          </div>
          <ReactApexChart type="donut" series={ownershipChart.series} options={ownershipChart.options} height={280} />
        </div>
        <div className="shdash__chart-card shdash__chart-card--lg">
          <div className="shdash__chart-title">
            <Icon name="chart" size={15} color="#02c3a6" />
            {t('shareholder.chartDividends')}
          </div>
          <ReactApexChart type="area" series={dividendsChart.series} options={dividendsChart.options} height={260} />
        </div>
      </div>

      <div className="shdash__charts-row">
        <div className="shdash__chart-card shdash__chart-card--md">
          <div className="shdash__chart-title">
            <Icon name="decisions" size={15} color="#02c3a6" />
            {t('shareholder.chartDecisionStatus')}
          </div>
          <ReactApexChart type="donut" series={decisionsStatusChart.series} options={decisionsStatusChart.options} height={250} />
        </div>
        <div className="shdash__chart-card shdash__chart-card--md">
          <div className="shdash__chart-title">
            <Icon name="report" size={15} color="#02c3a6" />
            {t('shareholder.chartYield')}
          </div>
          <ReactApexChart type="bar" series={dividendPctChart.series} options={dividendPctChart.options} height={250} />
        </div>
        <div className="shdash__chart-card shdash__chart-card--md">
          <div className="shdash__chart-title">
            <Icon name="shareholders" size={15} color="#02c3a6" />
            {t('shareholder.shareholdersList')}
          </div>
          <div className="shdash__shareholders">
            {shareholders.map((s, i) => (
              <div key={i} className={`shdash__sh-row${s.isMe ? ' shdash__sh-row--me' : ''}`}>
                <div className="shdash__sh-rank">{i + 1}</div>
                <div className="shdash__sh-avatar" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                  {s.name.charAt(0)}
                </div>
                <div className="shdash__sh-info">
                  <div className="shdash__sh-name">{s.name}{s.isMe && <span className="shdash__me-tag">{t('shareholder.youTag')}</span>}</div>
                  <div className="shdash__sh-shares">{Number(s.shares ?? 0).toLocaleString(numLocale)} {t('shareholder.sharesWord')}</div>
                </div>
                <div className="shdash__sh-pct" style={{ color: s.isMe ? '#02c3a6' : '#00334a' }}>{s.pct}%</div>
                <div className="shdash__sh-bar-wrap">
                  <div className="shdash__sh-bar" style={{ width: `${s.pct}%`, background: s.isMe ? '#02c3a6' : '#c8f0ea' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="shdash__decisions-card">
        <div className="shdash__section-header">
          <div className="shdash__chart-title">
            <Icon name="decisions" size={15} color="#02c3a6" />
            {t('shareholder.recentTitle')}
          </div>
          <button type="button" className="shdash__see-all" onClick={() => navigate('/shareholder/decisions')}>
            {t('dashboard.viewAll')} <Icon name="chevron-left" size={12} color="#02c3a6" />
          </button>
        </div>

        <div className="shdash__decisions-list">
          {recentDecisions.map((d, i) => {
            const st  = decisionStatusMap[d.status]  ?? decisionStatusMap.pending;
            const imp = impactMap[d.impact] ?? impactMap.medium;
            return (
              <div key={d.id} className="shdash__decision-row" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="shdash__decision-idx">{i + 1}</div>
                <div className="shdash__decision-body">
                  <div className="shdash__decision-title">{d.title}</div>
                  <div className="shdash__decision-date">
                    <Icon name="calendar" size={11} color="#b0bec5" /> {d.date}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  <span className="shdash__impact-chip" style={{ color: imp.color, background: imp.bg }}>{imp.label}</span>
                  <span className={`status-badge ${st.cls}`}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
